import React, { FC } from 'react';
import type { VehicleGraphData } from '@packages/domain/cvt';
import { createUseStyles } from 'react-jss';
import { CvtPlotConfig, generateCvtPlotConfigs } from '@web/utils/generate-cvt-plots';
import { Typography } from '@packages/ui/shared/typography';
import { useTranslation } from 'react-i18next';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-basic-dist-min';

const Plot = createPlotlyComponent(Plotly);

const useStyles = createUseStyles({
    plotContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 20,
        justifyContent: 'center',
        width: '100%',
        boxSizing: 'border-box',
    },
    plotWrapper: ({ isPdf }: { isPdf?: boolean }) => ({
        flex: '1 1 calc(33.33% - 20px)',
        maxWidth: isPdf ? 350 : 'calc(33.33% - 20px)',
        minWidth: isPdf ? 350 : 320,
        boxSizing: 'border-box',
    }),
    panelCenterContainer: {
        marginTop: 40,
    },
});

type CvtReportGraphProps = {
    data: VehicleGraphData[];
    isPdf?: boolean;
};

export const CvtReportGraph: FC<CvtReportGraphProps> = ({ data, isPdf }) => {
    const classes = useStyles({ isPdf });
    const { t } = useTranslation();
    const plotConfigs: CvtPlotConfig[] = generateCvtPlotConfigs(data, isPdf);

    const hasPlottedGraphs = plotConfigs.some((config) => config.data.length > 0);

    return (
        <>
            <div style={{ marginBottom: 24 }}>
                <Typography variant="h3">Graphs</Typography>
            </div>
            <div className={classes.plotContainer}>
                {!hasPlottedGraphs ? (
                    <div className={classes.panelCenterContainer}>
                        <Typography>{t('cvt:reportDetails.noGraphMessage')}</Typography>
                    </div>
                ) : (
                    plotConfigs.map(({ defect, data, config, layout }, ind) => (
                        <div className={classes.plotWrapper} key={`${defect}-${ind}`}>
                            <Plot
                                divId={`plot-${ind}`}
                                key={`${defect}-${ind}`}
                                data={data}
                                layout={layout}
                                config={config}
                                useResizeHandler={!isPdf}
                                style={{
                                    width: isPdf ? 350 : '100%',
                                    height: isPdf ? 350 : '100%',
                                }}
                            />
                        </div>
                    ))
                )}
            </div>
        </>
    );
};

export default CvtReportGraph;
